'use client'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichText from '@/components/RichText'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { fields } from './fields'
import { getClientSideURL } from '@/utilities/getURL'
import { cn } from '@/utilities/ui'
import { markLeadSubmitted, clearLeadSubmitted } from '@/utilities/adTracking'
import { fireLeadConversions } from '@/lib/gtag-conversions'
import { prepareEnhancedConversionData } from '@/lib/enhanced-conversions'

/** Underline-style controls: bottom border only (no box). */
const formFieldBottomBorderClasses =
  '!px-0 [&_[data-slot=input]]:rounded-none [&_[data-slot=input]]:border-0 [&_[data-slot=input]]:border-b [&_[data-slot=input]]:border-b-neutral-400 [&_[data-slot=input]]:shadow-none [&_[data-slot=input]]:focus-visible:ring-0 [&_[data-slot=input]]:focus-visible:outline-none [&_[data-slot=input]]:focus-visible:border-b-neutral-600 [&_[data-slot=textarea]]:rounded-none [&_[data-slot=textarea]]:border-0 [&_[data-slot=textarea]]:border-b [&_[data-slot=textarea]]:border-b-neutral-400 [&_[data-slot=textarea]]:shadow-none [&_[data-slot=textarea]]:focus-visible:ring-0 [&_[data-slot=textarea]]:focus-visible:outline-none [&_[data-slot=textarea]]:focus-visible:border-b-neutral-600 [&_[data-slot=select-trigger]]:rounded-none [&_[data-slot=select-trigger]]:border-0 [&_[data-slot=select-trigger]]:border-b [&_[data-slot=select-trigger]]:border-b-neutral-400 [&_[data-slot=select-trigger]]:shadow-none [&_[data-slot=select-trigger]]:focus-visible:ring-0 [&_[data-slot=select-trigger]]:focus-visible:outline-none [&_[data-slot=select-trigger]]:focus-visible:border-b-neutral-600'

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: FormType
  introContent?: DefaultTypedEditorState
}

export const FormBlock: React.FC<
  {
    id?: string
    /** Merged onto the outer layout wrapper (e.g. width when embedded in TabbedSection). */
    className?: string
    /** Border / padding shell around the form; defaults to theme `border-border`. */
    panelClassName?: string
    /** When true, removes extra space before the submit button (e.g. contact page layout). */
    compactFooter?: boolean
    /** Small note shown between the fields and the submit button. */
    footerNote?: React.ReactNode
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    form: formFromProps,
    form: { id: formID, confirmationType, redirect, submitButtonLabel } = {},
    introContent,
    className,
    panelClassName,
    footerNote,
  } = props

  const formMethods = useForm({
    defaultValues: formFromProps.fields,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          const target = (confirmationType === 'redirect' && redirect?.url) || '/thank-you'

          // Convert here rather than on arrival, so a CMS-configured redirect
          // to any URL is still counted. Only /thank-you can act as a fallback
          // sender, so for every other target we clear the flag unconditionally
          // — leaving it set would misfire on a later, unrelated /thank-you
          // visit in the same session.
          const enhancedData = await prepareEnhancedConversionData({
            email: dataToSend.find(({ field }) => field === 'email')?.value,
            phone: dataToSend.find(({ field }) => field === 'phone')?.value,
          })
          markLeadSubmitted('payload-form', enhancedData)
          const dispatched = fireLeadConversions(
            'payload-form',
            () => router.push(target),
            enhancedData,
          )
          if (dispatched || target !== '/thank-you') clearLeadSubmitted()
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType],
  )

  return (
    <div className={cn('w-full flex flex-col', className)}>
      {enableIntro && introContent && !hasSubmitted && (
        <RichText className="mb-8 lg:mb-12" data={introContent} enableGutter={false} />
      )}
      <div
        className={cn('px-0 flex-1 flex flex-col', formFieldBottomBorderClasses, panelClassName)}
      >
        <FormProvider {...formMethods}>
          {error && <div>{`${error.status || '500'}: ${error.message || ''}`}</div>}
          <form id={formID} onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1">
            <div className="mb-4 flex flex-wrap last:mb-0">
              {formFromProps &&
                formFromProps.fields &&
                formFromProps.fields?.map((field, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                  if (Field) {
                    const w = (field as { width?: number }).width
                    const widthStyle: React.CSSProperties =
                      w && w < 100
                        ? { width: `${w}%`, paddingRight: index % 2 === 0 ? '12px' : '0' }
                        : { width: '100%' }
                    return (
                      <div
                        className="mb-2 last:mb-0 [&_[data-slot=input]::placeholder]:text-[18px] [&_[data-slot=textarea]::placeholder]:text-[18px] [&_[data-slot=select-trigger][data-placeholder]]:text-[18px]"
                        key={index}
                        style={widthStyle}
                      >
                        <Field
                          form={formFromProps}
                          {...field}
                          width={100}
                          {...formMethods}
                          control={control}
                          errors={errors}
                          register={register}
                        />
                      </div>
                    )
                  }
                  return null
                })}
            </div>
            <div className="mt-auto pt-4">
              <button
                form={formID}
                type="submit"
                disabled={isLoading || hasSubmitted}
                className="w-full font-outfit text-white"
                style={{
                  background: '#009BAB',
                  border: 'none',
                  borderRadius: 0,
                  fontSize: '20px',
                  fontWeight: 900,
                  lineHeight: '20px',
                  maxHeight: '40px',
                  padding: '10px 0',
                  opacity: isLoading || hasSubmitted ? 0.6 : 1,
                  cursor: isLoading || hasSubmitted ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? 'Sending...' : hasSubmitted ? 'Redirecting...' : submitButtonLabel}
              </button>

              <p className="font-outfit text-[16px] leading-[24px] font-normal italic text-[#1d1d1b] mt-2 text-center">
                *Craft <span className="font-bold">never</span> shares or sells your information. We
                are a local &amp; trusted San Diego Contractor.
              </p>
            </div>

            {footerNote && (
              <p className="font-outfit text-[13px] italic text-[#1d1d1b]">{footerNote}</p>
            )}
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
